import { useState, useEffect } from "react";
import LogLayout from "../components/LogLayout";
import { Shield, ShieldCheck, BarChart3, EyeOff, Loader2, Mail, Lock, Check, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import axios from "axios";
import { getAuthHeaders } from "../lib/auth";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MyAccountPage() {
  const { user, refreshUser } = useAuth();
  const [twofaEnabled, setTwofaEnabled] = useState(user?.twofa_enabled || false);
  const [dataSharing, setDataSharing] = useState(user?.data_sharing_enabled !== false); // default true
  const [busy, setBusy] = useState(null); // "2fa" | "sharing" | null

  useEffect(() => {
    setTwofaEnabled(user?.twofa_enabled || false);
    setDataSharing(user?.data_sharing_enabled !== false);
  }, [user]);

  const toggleTwoFa = async () => {
    setBusy("2fa");
    try {
      const res = await axios.post(`${API}/auth/twofa/toggle`, { enabled: !twofaEnabled }, { headers: getAuthHeaders() });
      setTwofaEnabled(res.data.twofa_enabled);
      toast.success(res.data.message);
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(null);
    }
  };

  const toggleDataSharing = async () => {
    setBusy("sharing");
    try {
      const res = await axios.post(`${API}/auth/data-sharing/toggle`, { enabled: !dataSharing }, { headers: getAuthHeaders() });
      setDataSharing(res.data.data_sharing_enabled);
      toast.success(res.data.message);
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(null);
    }
  };

  return (
    <LogLayout title="Mon compte" subtitle="Securite et preferences de confidentialite">
      <div className="space-y-6 max-w-3xl" data-testid="my-account-page">
        {/* Bloc identité */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wider">Informations du compte</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] text-slate-500 uppercase">Nom complet</p>
              <p className="text-sm font-medium text-slate-800 mt-1">{user?.full_name || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase">Email</p>
              <p className="text-sm font-medium text-slate-800 mt-1">{user?.email || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase">Pharmacie / Etablissement</p>
              <p className="text-sm font-medium text-slate-800 mt-1">{user?.pharmacy_name || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase">Role</p>
              <p className="text-sm font-medium text-slate-800 mt-1 capitalize">{user?.role || "—"}</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100">
            <Link to="/forgot-password" className="text-xs text-sky-600 hover:underline inline-flex items-center gap-1.5" data-testid="change-password-link">
              <Lock className="w-3.5 h-3.5" /> Modifier mon mot de passe (lien envoye par email)
            </Link>
          </div>
        </div>

        {/* 2FA */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {twofaEnabled ? (
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                ) : (
                  <Shield className="w-5 h-5 text-slate-400" />
                )}
                <h3 className="text-sm font-semibold text-slate-800">Verification en 2 etapes (2FA email)</h3>
                {twofaEnabled && <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-bold">ACTIVEE</span>}
              </div>
              <p className="text-xs text-slate-500">
                A chaque connexion, un code a 6 chiffres sera envoye sur votre email. Vous devrez le saisir avant d'acceder au compte. Recommande pour les comptes admin et superadmin manipulant des donnees sensibles.
              </p>
              <div className="mt-3 flex items-center gap-3 text-[10px] text-slate-500">
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> Code envoye sur {user?.email}</span>
                <span>•</span>
                <span>Code valide 10 minutes</span>
              </div>
            </div>
            <Button
              onClick={toggleTwoFa}
              disabled={busy === "2fa"}
              data-testid="toggle-2fa-btn"
              className={twofaEnabled ? "bg-red-50 text-red-600 hover:bg-red-100 border border-red-200" : "bg-emerald-600 text-white hover:bg-emerald-500"}
            >
              {busy === "2fa" ? <Loader2 className="w-4 h-4 animate-spin" /> : (twofaEnabled ? <><X className="w-4 h-4 mr-1.5" />Desactiver</> : <><Check className="w-4 h-4 mr-1.5" />Activer</>)}
            </Button>
          </div>
        </div>

        {/* Data sharing preference */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {dataSharing ? (
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                ) : (
                  <EyeOff className="w-5 h-5 text-slate-400" />
                )}
                <h3 className="text-sm font-semibold text-slate-800">Partage de donnees anonymisees (benchmarks)</h3>
                {!dataSharing && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-bold">DESACTIVE</span>}
              </div>
              <p className="text-xs text-slate-500">
                Quand cette option est <strong>activee</strong>, vos chiffres (delais de paiement, recouvrement, volumes) sont inclus de maniere <strong>anonymisee</strong> dans les benchmarks zone et les classements CMES. Aucune donnee identifiante n'est jamais partagee.
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Si vous la desactivez, vos chiffres ne contribueront plus aux moyennes affichees aux autres pharmacies, et inversement vous ne pourrez plus voir les comparaisons en temps reel.
              </p>
            </div>
            <Button
              onClick={toggleDataSharing}
              disabled={busy === "sharing"}
              data-testid="toggle-data-sharing-btn"
              className={dataSharing ? "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200" : "bg-blue-600 text-white hover:bg-blue-500"}
            >
              {busy === "sharing" ? <Loader2 className="w-4 h-4 animate-spin" /> : (dataSharing ? <><EyeOff className="w-4 h-4 mr-1.5" />Retirer mes donnees</> : <><Check className="w-4 h-4 mr-1.5" />Participer</>)}
            </Button>
          </div>
        </div>
      </div>
    </LogLayout>
  );
}
